import PropTypes from 'prop-types';

/**
 * Authoring UI for the `sumEquals` top-level schema key.
 *
 * Production shape (1 occurrence in admin_buildex_dev — manpower_distribution):
 *   {
 *     fields: string[],            // root-level fields contributing to the sum
 *     arrayField: string,
 *     arrayFieldProp: string,      // numeric prop on each item of arrayField
 *     total: number,
 *   }
 *
 * Semantics: sum( fields[*] ) + sum( arrayField[*].arrayFieldProp ) === total
 * (The Go runtime owns the exact semantics; the editor just round-trips
 * the four authored values.)
 *
 * UX: comma-separated `fields` (matches how arrays are entered elsewhere
 * in this builder, e.g. `enumValues`), + three additional inputs.
 */
export default function SumEqualsForm({
	value,
	onChange,
	disabled = false,
	fieldOptions = [],
}) {
	const safe = value && typeof value === 'object' ? value : {};
	const fieldsAsText = Array.isArray(safe.fields) ? safe.fields.join(', ') : '';

	const update = (patch) => {
		const merged = { ...safe, ...patch };
		const nonEmpty = (Array.isArray(merged.fields) && merged.fields.length > 0)
			|| merged.arrayField
			|| merged.arrayFieldProp
			|| merged.total != null;
		onChange(nonEmpty ? merged : null);
	};

	const updateFields = (text) => {
		const fields = text
			.split(',')
			.map((s) => s.trim())
			.filter(Boolean);
		update({ fields });
	};

	const updateTotal = (text) => {
		if (text === '') {
			update({ total: undefined });
			return;
		}
		const n = Number(text);
		update({ total: Number.isNaN(n) ? text : n });
	};

	const clear = () => onChange(null);

	const fieldListId = 'cv-se-field-options';
	const isAuthored = fieldsAsText
		|| safe.arrayField
		|| safe.arrayFieldProp
		|| (safe.total != null && safe.total !== '');

	return (
		<div className="dsv-cv-form" data-testid="cv-se-form">
			<p className="dsv-cv-form__hint">
				Use this when selected number fields must add up to an expected
				total, such as percentage splits that must equal 100.
			</p>

			<datalist id={fieldListId}>
				{fieldOptions.map((opt) => <option key={opt} value={opt} />)}
			</datalist>

			<div className="dsv-modal__field">
				<label className="dsv-modal__label">Root Fields (comma-separated)</label>
				<input
					className="dsv-modal__input"
					type="text"
					value={fieldsAsText}
					disabled={disabled}
					onChange={(e) => updateFields(e.target.value)}
					placeholder="e.g. manpower_distribution_storage_ratio"
				/>
			</div>

			<div className="dsv-modal__field-row">
				<div className="dsv-modal__field dsv-modal__field--half">
					<label className="dsv-modal__label">Array Field</label>
					<input
						className="dsv-modal__input"
						type="text"
						list={fieldListId}
						value={safe.arrayField ?? ''}
						disabled={disabled}
						onChange={(e) => update({ arrayField: e.target.value })}
						placeholder="e.g. manpower_distribution_client_details"
					/>
				</div>
				<div className="dsv-modal__field dsv-modal__field--half">
					<label className="dsv-modal__label">Item Numeric Prop</label>
					<input
						className="dsv-modal__input"
						type="text"
						value={safe.arrayFieldProp ?? ''}
						disabled={disabled}
						onChange={(e) => update({ arrayFieldProp: e.target.value })}
						placeholder="e.g. manpower_ratio"
					/>
				</div>
			</div>

			<div className="dsv-modal__field">
				<label className="dsv-modal__label">Total</label>
				<input
					className="dsv-modal__input"
					type="number"
					value={safe.total ?? ''}
					disabled={disabled}
					onChange={(e) => updateTotal(e.target.value)}
					placeholder="e.g. 100"
				/>
			</div>

			{!disabled && isAuthored && (
				<button
					type="button"
					className="dsv-btn dsv-btn--secondary"
					onClick={clear}
					data-testid="cv-se-clear"
				>
					Clear
				</button>
			)}
		</div>
	);
}

SumEqualsForm.propTypes = {
	value: PropTypes.object,
	onChange: PropTypes.func.isRequired,
	disabled: PropTypes.bool,
	fieldOptions: PropTypes.arrayOf(PropTypes.string),
};
